import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { hasPermission, type PermissionBag, type Tenant, type Workspace } from '@orqforge/shared';
import { apiClient, type AccessMe } from '../lib/api-client';
import { checkHealth, bindUnavailable } from '../lib/http';
import { displayName, getSessionUser, logout, subscribeSession, type SessionUser } from '../lib/session';

const TENANT_KEY = 'orqforge.tenantId';
const WORKSPACE_KEY = 'orqforge.workspaceId';
const THEME_KEY = 'orqforge.theme';

interface AppState {
  ready: boolean;
  maintenance: boolean;
  user: SessionUser;
  name: string;
  access: AccessMe | null;
  tenants: Tenant[];
  tenantId: string | null;
  workspaces: Workspace[];
  workspaceId: string | null;
  dark: boolean;
  can: (page: string, action: string) => boolean;
  setTenant: (id: string) => void;
  setWorkspace: (id: string) => void;
  toggleTheme: () => void;
  logout: () => void;
  reloadScope: () => Promise<void>;
}

const AppContext = createContext<AppState | null>(null);

function readThemeDark(): boolean {
  const stored = localStorage.getItem(THEME_KEY) ?? localStorage.getItem('anvil.theme');
  if (stored) return !stored.includes('light');
  return true;
}

function applyTheme(dark: boolean): void {
  document.documentElement.classList.toggle('dark', dark);
  document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
  localStorage.setItem(THEME_KEY, dark ? 'orqforge-dark' : 'orqforge-light');
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [maintenance, setMaintenance] = useState(false);
  const [user, setUser] = useState(getSessionUser());
  const [access, setAccess] = useState<AccessMe | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenantId, setTenantId] = useState<string | null>(localStorage.getItem(TENANT_KEY));
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [workspaceId, setWorkspaceId] = useState<string | null>(localStorage.getItem(WORKSPACE_KEY));
  const [dark, setDark] = useState(readThemeDark);

  useEffect(() => subscribeSession(() => setUser(getSessionUser())), []);
  useEffect(() => applyTheme(dark), [dark]);

  const reloadScope = useCallback(async () => {
    const me = await apiClient.getAccessMe();
    setAccess(me);
    const session = getSessionUser();
    const all = session.isOrqForgeAdmin || me.allTenants || me.isOrqForgeAdmin;
    let items: Tenant[] = [];
    try {
      items = all ? await apiClient.listAllTenants() : await apiClient.listMyTenants();
    } catch {
      items = await apiClient.listMyTenants();
    }
    setTenants(items);
    const current = localStorage.getItem(TENANT_KEY);
    const active = items.find((t) => t.id === current && t.status === 'active');
    const next = active?.id ?? items.find((t) => t.status === 'active')?.id ?? null;
    if (next) localStorage.setItem(TENANT_KEY, next);
    else localStorage.removeItem(TENANT_KEY);
    setTenantId(next);
    if (next !== current) localStorage.removeItem(WORKSPACE_KEY);
    const spaces = next ? await apiClient.listWorkspaces() : [];
    setWorkspaces(spaces);
    const wsCurrent = localStorage.getItem(WORKSPACE_KEY);
    const wsValid = spaces.find((w) => w.id === wsCurrent);
    const wsNext = wsValid?.id ?? spaces.find((w) => w.status === 'live')?.id ?? spaces[0]?.id ?? null;
    if (wsNext) localStorage.setItem(WORKSPACE_KEY, wsNext);
    else localStorage.removeItem(WORKSPACE_KEY);
    setWorkspaceId(wsNext);
  }, []);

  useEffect(() => {
    bindUnavailable(() => setMaintenance(true));
    let timer: ReturnType<typeof setInterval>;
    const tick = async () => {
      const ok = await checkHealth();
      setMaintenance(!ok);
      setReady(true);
    };
    void tick();
    timer = setInterval(() => void tick(), 30000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (maintenance || !user.authenticated) return;
    void reloadScope().catch(() => setMaintenance(true));
  }, [maintenance, user.authenticated, reloadScope]);

  const can = useCallback(
    (page: string, action: string) => {
      if (!access) return true;
      if (access.allTenants || access.isOrqForgeAdmin) return true;
      return hasPermission(access.permissions as PermissionBag, page, action);
    },
    [access],
  );

  const value = useMemo<AppState>(
    () => ({
      ready,
      maintenance,
      user,
      name: displayName(),
      access,
      tenants,
      tenantId,
      workspaces,
      workspaceId,
      dark,
      can,
      setTenant: (id: string) => {
        localStorage.setItem(TENANT_KEY, id);
        localStorage.removeItem(WORKSPACE_KEY);
        window.location.reload();
      },
      setWorkspace: (id: string) => {
        localStorage.setItem(WORKSPACE_KEY, id);
        window.location.reload();
      },
      toggleTheme: () => setDark((d) => !d),
      logout,
      reloadScope,
    }),
    [ready, maintenance, user, access, tenants, tenantId, workspaces, workspaceId, dark, can, reloadScope],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp(): AppState {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp outside provider');
  return ctx;
}
